/**
 * Кастомные хуки для работы с API справочников через React Query
 */

import { useQuery } from '@tanstack/react-query';
import {
  fetchRegions,
  fetchCountries,
  fetchSex,
  fetchMaritalStatuses,
  fetchRejectionReasons,
  fetchTerritorialDirections,
  fetchPointsOfSale,
} from '../api/ReferenceApi';
import type { Region, Country, Sex, MaritalStatus, TerritorialDirection, PointOfSale } from '../types';

/**
 * Хук для получения списка регионов
 */
export const useRegions = () => {
  return useQuery<Region[]>({
    queryKey: ['regions'],
    queryFn: fetchRegions,
    staleTime: 60 * 60 * 1000, // 1 час - справочники редко меняются
  });
};

/**
 * Хук для получения списка стран
 */
export const useCountries = () => {
  return useQuery<Country[]>({
    queryKey: ['countries'],
    queryFn: fetchCountries,
    staleTime: 60 * 60 * 1000,
  });
};

/**
 * Хук для получения справочника пола
 */
export const useSex = () => {
  return useQuery<Sex[]>({
    queryKey: ['sex'],
    queryFn: fetchSex,
    staleTime: 60 * 60 * 1000,
  });
};

/**
 * Хук для получения справочника семейного положения
 */
export const useMaritalStatuses = () => {
  return useQuery<MaritalStatus[]>({
    queryKey: ['maritalStatuses'],
    queryFn: fetchMaritalStatuses,
    staleTime: 60 * 60 * 1000,
  });
};

/**
 * Хук для получения причин отказа
 */
export const useRejectionReasons = (rejectedByClient: boolean) => {
  return useQuery({
    queryKey: ['rejectionReasons', rejectedByClient],
    queryFn: () => fetchRejectionReasons(rejectedByClient),
    staleTime: 30 * 60 * 1000, // 30 минут
  });
};

/**
 * Хук для получения территориальных дирекций
 */
export const useTerritorialDirections = () => {
  return useQuery<TerritorialDirection[]>({
    queryKey: ['territorialDirections'],
    queryFn: fetchTerritorialDirections,
    staleTime: 60 * 60 * 1000,
  });
};

/**
 * Хук для получения точек продаж по территориальной дирекции
 */
export const usePointsOfSale = (territorialDirectionId?: number) => {
  return useQuery<PointOfSale[]>({
    queryKey: ['pointsOfSale', territorialDirectionId],
    queryFn: () => fetchPointsOfSale(territorialDirectionId!),
    enabled: !!territorialDirectionId, // Запрос только если передан ID
    staleTime: 30 * 60 * 1000,
  });
};
